import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetMyProfileQuery, useMarcarAvisoVistoMutation } from '../../store/api/usersApi';
import { useGetAnuncioPendienteQuery, useRegistrarAnuncioMutation } from '../../store/api/anunciosApi';
import { AvisoLamina } from './AvisoLamina';
import { AvisoNotificacion } from './AvisoNotificacion';

/**
 * Muestra el anuncio pendiente de este usuario, si hay alguno.
 *
 * (28/08/2026) El anuncio ahora VIENE DEL SERVIDOR, que resuelve a quién le toca
 * (`anuncios/elegibilidad.ts`). Antes era una lista en el código: cada anuncio pedía un
 * deploy, y de lo que pasaba después no se sabía nada.
 *
 * Se gatea por `user.avisosVistos` —BASE DE DATOS, no localStorage— así que se ve una sola
 * vez por persona en cualquier dispositivo. Además queda registrado qué hizo con él:
 * mostrado, cerrado y si fue al destino. Esa diferencia es la que dice si el anuncio se
 * leyó o se sacó de encima.
 *
 * ⛔ NO se monta encima del onboarding. Un agente nuevo tiene el onboarding pendiente Y
 * todos los anuncios sin ver: los dos overlays a la vez son una pared antes de haber
 * entrado. Primero el onboarding; los anuncios esperan al próximo ingreso.
 */

/**
 * Cada cuánto se vuelve a preguntar si hay un anuncio nuevo, para el que YA está adentro.
 *
 * Sin esto el anuncio solo aparecía al entrar: quien dejó el CRM abierto —la mayoría en un
 * día de trabajo— no se enteraba hasta el próximo login. Justo el caso del cambio de
 * número, donde la demora se paga con mensajes a una línea dada de baja.
 *
 * 5 minutos: un anuncio no es urgente al segundo, y con ~120 agentes es menos de medio
 * request por segundo. `refetchOnFocus` cubre volver a la pestaña sin esperar al tick.
 */
const CADA = 5 * 60 * 1000;

export function AvisoGate() {
  const { data: profile } = useGetMyProfileQuery();
  const onboardingPendiente = !!profile && profile.hasCompletedOnboarding !== true;

  const { data: anuncio } = useGetAnuncioPendienteQuery(undefined, {
    skip: !profile || onboardingPendiente,
    pollingInterval: CADA,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const [marcarVisto] = useMarcarAvisoVistoMutation();
  const [registrar] = useRegistrarAnuncioMutation();
  const [cerrados, setCerrados] = useState<string[]>([]);
  const navigate = useNavigate();

  // Que se haya MOSTRADO se registra una sola vez por clave, aunque el componente se vuelva
  // a renderizar: sin esto el polling y cada re-render inflarían el número de vistas.
  const yaContado = useRef<Set<string>>(new Set());
  const clave = anuncio?.clave;
  const visible = !!anuncio && !cerrados.includes(anuncio.clave);

  useEffect(() => {
    if (!visible || !clave || yaContado.current.has(clave)) return;
    yaContado.current.add(clave);
    registrar({ clave, accion: 'mostrado' });
  }, [visible, clave, registrar]);

  if (!anuncio || !visible) return null;

  const cerrar = async (abrirGustavo: boolean, irA?: string) => {
    // Se saca de pantalla YA; la persistencia va en paralelo. Si el POST falla, el anuncio
    // vuelve en el próximo ingreso: molesto, pero nunca se pierde el mensaje.
    setCerrados((c) => [...c, anuncio.clave]);
    registrar({ clave: anuncio.clave, accion: irA || abrirGustavo ? 'destino' : 'cerrado' });
    try { await marcarVisto(anuncio.clave).unwrap(); } catch { /* reintenta solo la próxima vez */ }
    if (abrirGustavo) {
      setTimeout(() => window.dispatchEvent(new CustomEvent('gustavo:open')), 280);
    }
    // El botón que lleva a una pantalla navega DESPUÉS de sacar la lámina, para que no se
    // vea el overlay encima de la pantalla nueva mientras se desmonta.
    if (irA) setTimeout(() => navigate(irA), 200);
  };

  /**
   * Dos formas de mostrar el mismo anuncio, y las decide quien lo escribe (`tipo`):
   *  · `notificacion` — tarjeta en la esquina que NO frena y NO se va sola; se queda de
   *    pantalla en pantalla (el gate vive en el layout) hasta que la persona la cierre.
   *  · `lamina` — el modal a pantalla completa de siempre. Es el default: un anuncio viejo
   *    sin `tipo` se sigue viendo igual.
   */
  return anuncio.tipo === 'notificacion'
    ? <AvisoNotificacion aviso={anuncio} onCerrar={cerrar} />
    : <AvisoLamina aviso={anuncio} onCerrar={cerrar} />;
}
